import React, {useState, useRef} from 'react';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Button} from 'primereact/button';
import {Tooltip} from 'primereact/tooltip';
import {Calendar} from 'primereact/calendar';
import {InputText} from 'primereact/inputtext';
import {Toast} from 'primereact/toast';
import axios from 'axios';
import {TieredMenu} from 'primereact/tieredmenu';

import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import StorageHelper from '../../../utils/storageHelper';

const DeviceReportsTable = () => {
    const [payments, setPayments] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [loading, setLoading] = useState(false);
    const [fromDate, setFromDate] = useState(() => new Date());
    const [toDate, setToDate] = useState(() => new Date());
    const [serialNumber, setSerialNumber] = useState('');

    const dt = useRef(null);
    const menu = useRef(null);
    const toast = useRef(null);

    const fetchDeviceReport = async () => {
        if (!serialNumber || serialNumber.trim() === '') {
            toast.current.show({
                severity: 'warn',
                summary: 'Missing Serial Number',
                detail: 'Please enter a serial number',
                life: 3000
            });
            return;
        }

        setLoading(true);
        try {
            const formattedFromDate = fromDate.toISOString().split('T')[0];
            const formattedToDate = toDate.toISOString().split('T')[0];

            const response = await axios.get(
                `${process.env.REACT_APP_DOVESAPI}report/device`,
                {
                    params: {
                        serialNumber: serialNumber.trim(),
                        from: formattedFromDate,
                        to: formattedToDate
                    },
                    headers: {
                        Authorization: 'Bearer ' + StorageHelper.get('token')
                    }
                }
            );

            if (response.data.status === "SUCCESS") {
                setPayments(response.data.responseBody);
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Device report loaded successfully',
                    life: 3000
                });
            } else {
                console.error('Failed to fetch device report:', response.data.status);
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to fetch device report',
                    life: 3000
                });
            }
        } catch (error) {
            console.error('Error fetching device report:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch device report. Please try again.',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    const exportCSV = (selectionOnly) => {
        dt.current.exportCSV({selectionOnly, filename: serialNumber || "device-report"});
    };

    const onGlobalFilter = (event) => {
        setGlobalFilter(event.target.value);
    };

    const amountBodyTemplate = (rowData) => {
        return `${parseFloat(rowData.amount || 0).toFixed(2)}`;
    };

    const dateBodyTemplate = (rowData, field) => {
        const date = rowData[field];
        if (!date) return '-';
        return new Date(date).toLocaleString();
    };

    const paymentStatusBodyTemplate = (rowData) => {
        const getStatusStyle = (status) => {
            if (!status) return {color: '#A0A0A0', backgroundColor: '#F5F5F5'};

            const upperStatus = status.toUpperCase();
            switch (upperStatus) {
                case 'PENDING':
                    return {color: '#FCB700', backgroundColor: '#FFF8E1'};
                case 'SUCCESS':
                case 'SUCCESSFUL':
                case 'COMPLETED':
                    return {color: '#00D390', backgroundColor: '#E6FFF7'};
                case 'FAILED':
                case 'DECLINED':
                    return {color: '#FF6266', backgroundColor: '#FFECEC'};
                case 'PROCESSING':
                    return {color: '#00BAFE', backgroundColor: '#E6F7FF'};
                default:
                    if (upperStatus.includes('NMB') || upperStatus.includes('CR')) {
                        return {color: '#00D390', backgroundColor: '#E6FFF7'};
                    }
                    return {color: '#A0A0A0', backgroundColor: '#F5F5F5'};
            }
        };

        return (
            <span
                style={{
                    ...getStatusStyle(rowData.paymentStatus),
                    padding: '0.25rem 0.75rem',
                    borderRadius: '0.375rem',
                    fontWeight: '500',
                    fontSize: '0.875rem'
                }}
            >
                {rowData.paymentStatus || 'N/A'}
            </span>
        );
    };

    const globalFilterElement = (
        <InputText
            placeholder="Search payments..."
            value={globalFilter}
            onChange={onGlobalFilter}
            style={{width: '100%'}}
        />
    );

    const items = [
        {
            label: 'Download CSV',
            icon: 'pi pi-file-excel',
            command: () => {
                exportCSV(false);
            }
        },
    ];

    return (
        <div className="card home-chart">
            <Toast ref={toast}/>
            <Tooltip target=".export-buttons>button" position="bottom"/>

            <div className="card-header">
                <h2 className="card-title home-chart">POS Device Report</h2>
            </div>

            <div className="card-body">
                <div
                    style={{
                        marginBottom: '1.5rem',
                        padding: '1.5rem',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef',
                        width: '100%'
                    }}
                >
                    <div
                        className="flex"
                        style={{
                            gap: '1.5rem',
                            marginBottom: '1.5rem',
                            flexWrap: 'nowrap',
                            width: '100%'
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.4rem',
                                minWidth: '240px',
                                flex: '0 0 auto'
                            }}
                        >
                            <label
                                htmlFor="serialNumber"
                                style={{
                                    fontWeight: '600',
                                    fontSize: '0.8rem',
                                    color: '#495057'
                                }}
                            >
                                Serial Number
                            </label>
                            <InputText
                                id="serialNumber"
                                value={serialNumber}
                                onChange={(e) => setSerialNumber(e.target.value)}
                                placeholder="Enter device serial number"
                                style={{width: '100%'}}
                            />
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.4rem',
                                minWidth: '220px',
                                flex: '0 0 auto'
                            }}
                        >
                            <label
                                htmlFor="fromDate"
                                style={{
                                    fontWeight: '600',
                                    fontSize: '0.8rem',
                                    color: '#495057'
                                }}
                            >
                                From Date
                            </label>
                            <Calendar
                                id="fromDate"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.value)}
                                dateFormat="yy-mm-dd"
                                showIcon
                                placeholder="Select start date"
                                style={{width: '100%'}}
                            />
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.4rem',
                                minWidth: '220px',
                                flex: '0 0 auto'
                            }}
                        >
                            <label
                                htmlFor="toDate"
                                style={{
                                    fontWeight: '600',
                                    fontSize: '0.8rem',
                                    color: '#495057'
                                }}
                            >
                                To Date
                            </label>
                            <Calendar
                                id="toDate"
                                value={toDate}
                                onChange={(e) => setToDate(e.value)}
                                dateFormat="yy-mm-dd"
                                showIcon
                                placeholder="Select end date"
                                style={{width: '100%'}}
                            />
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.4rem',
                                flex: '1 1 auto',
                                minWidth: '260px'
                            }}
                        >
                            <label
                                style={{
                                    fontWeight: '600',
                                    fontSize: '0.8rem',
                                    color: '#495057'
                                }}
                            >
                                Search
                            </label>
                            <span className="p-input-icon-left" style={{width: '100%'}}>
                                <i className="pi pi-search"/>
                                {globalFilterElement}
                            </span>
                        </div>
                    </div>

                    <div className="flex justify-content-end" style={{gap: '0.75rem', width: '100%'}}>
                        <Button
                            label="Generate Report"
                            className="m-2"
                            icon="pi pi-refresh"
                            onClick={fetchDeviceReport}
                            loading={loading}
                            style={{whiteSpace: 'nowrap'}}
                        />
                        <TieredMenu model={items} popup ref={menu} breakpoint="767px"/>
                        <Button
                            label="Download"
                            className="m-2"
                            icon="pi pi-download"
                            onClick={(e) => menu.current.toggle(e)}
                            disabled={payments.length === 0}
                            severity="secondary"
                            style={{whiteSpace: 'nowrap'}}
                        />
                    </div>
                </div>

                <DataTable
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    ref={dt}
                    value={payments}
                    loading={loading}
                    tableStyle={{minWidth: '50rem'}}
                    globalFilter={globalFilter}
                    exportFilename={serialNumber}
                    emptyMessage="No payment records found. Please enter a serial number, select a date range, and generate report."
                >
                    <Column field="policyNumber" header="Policy Number" sortable style={{minWidth: '150px'}}/>
                    <Column field="productName" header="Product Name" sortable style={{minWidth: '200px'}}/>
                    <Column field="payerName" header="Payer Name" sortable style={{minWidth: '180px'}}/>
                    <Column field="mobileNumber" header="Mobile Number" sortable style={{minWidth: '150px'}}/>
                    <Column field="emailAddress" header="Email" sortable style={{minWidth: '200px'}}/>
                    <Column
                        field="paymentMethod"
                        header="Payment Method"
                        sortable
                        style={{minWidth: '150px'}}
                    />
                    <Column
                        field="amount"
                        header="Amount"
                        body={amountBodyTemplate}
                        sortable
                        style={{minWidth: '120px'}}
                    />
                    <Column
                        field="paymentStatus"
                        header="Payment Status"
                        body={paymentStatusBodyTemplate}
                        sortable
                        style={{minWidth: '150px'}}
                    />
                    <Column field="notes" header="Notes" style={{minWidth: '200px'}}/>
                    <Column
                        field="timeStamp"
                        header="Timestamp"
                        body={(rowData) => dateBodyTemplate(rowData, 'timeStamp')}
                        sortable
                        style={{minWidth: '200px'}}
                    />
                </DataTable>
            </div>
        </div>
    );
};

export default DeviceReportsTable;